import { FilterChip, FilterGroup } from '@src/components/shared/FilterChip';
import { PageHeader } from '@src/components/shared/PageHeader';
import { Badge } from '@src/components/ui/badge';
import { Button } from '@src/components/ui/button';
import { Card } from '@src/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@src/components/ui/dialog';
import { Input } from '@src/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@src/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@src/components/ui/table';
import { errorMessage } from '@src/lib/errors';
import { formatCost, formatDuration, formatRunDate, formatUtcTooltip, statusBadgeVariant, statusLabel } from '@src/lib/runFormat';
import { cn } from '@src/lib/utils';
import { resources } from '@src/resources';
import {
  type CostBand,
  type DateWindow,
  ENRICHMENT_SCOPES,
  fetchRuns,
  hasEnrichmentDetailRoute,
  Outcome,
  outcomeLabel,
  type RunRow,
  type RunsQueryParams,
  type RunStatus,
  scopeLabel,
} from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, ZoomIn } from 'lucide-react';
import { type ChangeEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

/** The preset window chips, plus a frontend-only 'custom' mode -- when selected, `since`/`until`
 * are sent directly instead of `window` (see queryParams below). Not part of the API's DateWindow
 * enum, which only knows the 4 presets. */
type WindowSelection = DateWindow | 'custom';

const DEFAULT_WINDOW: WindowSelection = 'all';

const WINDOW_OPTIONS: { value: WindowSelection; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

const STATUS_OPTIONS: { value?: RunStatus; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

/** Built by iterating the real `Outcome` enum -- not a hand-typed list -- so a member added later
 * (axetay/really#67) shows up here with no change to this file. */
const OUTCOME_OPTIONS: { value?: string; label: string }[] = [
  { value: undefined, label: 'All' },
  ...Object.values(Outcome).map((value) => ({ value, label: outcomeLabel(value) })),
];

/** Radix `Select.Item` rejects an empty-string value (used to mean "no filter" everywhere else
 * in this file) -- this sentinel stands in for it in the scope filter's Select. */
const ALL_SCOPES_VALUE = '__all__';

const COST_BAND_OPTIONS: { value?: CostBand; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'free', label: 'Free' },
  { value: 'under-0.01', label: '<$0.01' },
  { value: '0.01-0.05', label: '$0.01–$0.05' },
  { value: '0.05-0.20', label: '$0.05–$0.20' },
  { value: 'over-0.20', label: '>$0.20' },
];

/** Error/skip-reason text only -- the outcome itself gets its own badge in the Status/Outcome
 * column, so it's deliberately not repeated here. */
function resultText(row: RunRow): string {
  return row.error ?? row.message ?? '—';
}

/** Above this length the cell truncates and a click is needed to read the rest, so only then is
 * it worth turning into a link -- a short message already reads fine inline. */
const RESULT_LINK_THRESHOLD = 30;

/** Links to the enrichment detail view (input/output/prompt) for this run's property/scope -- like
 * really-data-ui's Property Inspector, axetay/really packages/really-data/ui. Query params, not a
 * runId path param: the detail view is keyed by property/scope (there's no per-run enrichment
 * record, only a per-run ledger row -- see RunDetail.tsx), and this keeps the link shareable. */
function runDetailHref(row: RunRow): string {
  const params = new URLSearchParams({ runId: row.runId, propertyId: row.propertyId, scope: row.scope, createdAt: row.createdAt });
  return `/runs/detail?${params.toString()}`;
}

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function logLevelClassName(level: string): string {
  if (level === 'error') return 'text-status-critical';
  if (level === 'warn') return 'text-status-warning';
  return 'text-muted-foreground';
}

export function Runs() {
  // Every filter lives in the URL (not useState) so the current view is shareable/bookmarkable
  // and survives a refresh or back/forward navigation.
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = (key: string, value: string | undefined) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  };

  const dateWindow = (searchParams.get('window') as WindowSelection | null) ?? DEFAULT_WINDOW;
  const scope = searchParams.get('scope') ?? undefined;
  const status = (searchParams.get('status') as RunStatus | null) ?? undefined;
  const outcome = searchParams.get('outcome') ?? undefined;
  const costBand = (searchParams.get('costBand') as CostBand | null) ?? undefined;
  const propertyId = searchParams.get('propertyId') ?? '';
  // datetime-local input values ('' when unset), only meaningful when dateWindow === 'custom'.
  const customFrom = searchParams.get('from') ?? '';
  const customTo = searchParams.get('to') ?? '';

  const setDateWindow = (value: WindowSelection) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_WINDOW) {
          next.delete('window');
        } else {
          next.set('window', value);
        }
        if (value !== 'custom') {
          next.delete('from');
          next.delete('to');
        }
        return next;
      },
      { replace: true },
    );
  };
  const setScope = (value: string | undefined) => setParam('scope', value);
  const setStatus = (value: RunStatus | undefined) => setParam('status', value);
  const setOutcome = (value: string | undefined) => setParam('outcome', value);
  const setCostBand = (value: CostBand | undefined) => setParam('costBand', value);
  const setPropertyId = (value: string) => setParam('propertyId', value);
  const setCustomFrom = (value: string) => setParam('from', value);
  const setCustomTo = (value: string) => setParam('to', value);

  const [openLogsRunId, setOpenLogsRunId] = useState<string | null>(null);
  const [openResultRunId, setOpenResultRunId] = useState<string | null>(null);
  const [copiedRunId, setCopiedRunId] = useState<string | null>(null);

  const copyRunId = (runId: string) => {
    void navigator.clipboard.writeText(runId);
    setCopiedRunId(runId);
    setTimeout(() => setCopiedRunId((current) => (current === runId ? null : current)), 1200);
  };

  const trimmedPropertyId = propertyId.trim();
  const isCustomWindow = dateWindow === 'custom';
  // datetime-local gives a value with no timezone offset (interpreted as local time by `new Date()`,
  // which is what we want -- the picker itself is already showing the user's local time).
  const customSince = isCustomWindow && customFrom ? new Date(customFrom).toISOString() : undefined;
  const customUntil = isCustomWindow && customTo ? new Date(customTo).toISOString() : undefined;

  const queryParams: RunsQueryParams = {
    window: isCustomWindow ? undefined : dateWindow,
    since: customSince,
    until: customUntil,
    scope,
    status,
    outcome,
    costBand,
    propertyId: trimmedPropertyId || undefined,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops-runs', queryParams],
    queryFn: () => fetchRuns(queryParams),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const hasActiveFilters =
    dateWindow !== DEFAULT_WINDOW ||
    scope !== undefined ||
    status !== undefined ||
    outcome !== undefined ||
    costBand !== undefined ||
    trimmedPropertyId !== '';

  const clearFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  const openLogsRun = items.find((row) => row.runId === openLogsRunId) ?? null;
  const openResultRun = items.find((row) => row.runId === openResultRunId) ?? null;

  const resultsLabel = isLoading
    ? resources.common.loading
    : total > items.length
      ? `Showing ${items.length} of ${total.toLocaleString()} runs`
      : `${total.toLocaleString()} run${total === 1 ? '' : 's'} matched`;

  return (
    <div>
      <PageHeader title={resources.pages.runs.title} subtitle={resources.pages.runs.subtitle} />

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-end gap-6">
          <FilterGroup label={resources.runs.windowLabel}>
            {WINDOW_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                active={dateWindow === opt.value}
                onClick={() => setDateWindow(opt.value)}
              />
            ))}
          </FilterGroup>

          {isCustomWindow && (
            <FilterGroup label={resources.runs.customRangeLabel}>
              <Input
                type="datetime-local"
                value={customFrom}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomFrom(event.target.value)}
                className="w-[190px]"
                aria-label={resources.runs.customFromLabel}
              />
              <span className="text-xs text-muted-foreground">{resources.runs.customRangeSeparator}</span>
              <Input
                type="datetime-local"
                value={customTo}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setCustomTo(event.target.value)}
                className="w-[190px]"
                aria-label={resources.runs.customToLabel}
              />
            </FilterGroup>
          )}

          <FilterGroup label={resources.runs.statusLabel}>
            {STATUS_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.label}
                label={opt.label}
                active={status === opt.value}
                onClick={() => setStatus(opt.value)}
              />
            ))}
          </FilterGroup>

          <FilterGroup label={resources.runs.outcomeLabel}>
            {OUTCOME_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.label}
                label={opt.label}
                active={outcome === opt.value}
                onClick={() => setOutcome(opt.value)}
              />
            ))}
          </FilterGroup>

          <FilterGroup label={resources.runs.costBandLabel}>
            {COST_BAND_OPTIONS.map((opt) => (
              <FilterChip
                key={opt.label}
                label={opt.label}
                active={costBand === opt.value}
                onClick={() => setCostBand(opt.value)}
              />
            ))}
          </FilterGroup>

          <FilterGroup label={resources.runs.scopeLabel}>
            <Select
              value={scope ?? ALL_SCOPES_VALUE}
              onValueChange={(value) => setScope(value === ALL_SCOPES_VALUE ? undefined : value)}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SCOPES_VALUE}>{resources.runs.allScopes}</SelectItem>
                {ENRICHMENT_SCOPES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {scopeLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterGroup>

          <FilterGroup label={resources.runs.propertyIdLabel}>
            <Input
              value={propertyId}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setPropertyId(event.target.value)}
              placeholder={resources.runs.propertyIdPlaceholder}
              className="w-40"
            />
          </FilterGroup>

          {hasActiveFilters && (
            <Button
              type="button"
              variant="link"
              onClick={clearFilters}
              className="h-auto p-0 pb-1.5 text-[11.5px] font-medium"
            >
              {resources.runs.clearFilters}
            </Button>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-xs text-muted-foreground">{resultsLabel}</span>
        </div>

        {isError && (
          <div className="p-8 text-sm text-status-critical">
            <p>{resources.runs.loadError}</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{errorMessage(error)}</p>
          </div>
        )}

        {!isError && !isLoading && items.length === 0 && (
          <div className="p-8 text-sm text-muted-foreground">{resources.runs.noResults}</div>
        )}

        {!isError && items.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{resources.runs.columns.runId}</TableHead>
                  {/* Sorted desc by this column server-side (ORDER BY created_at DESC) -- placed
                      right after Run ID so it's visible without scrolling past Duration/Cost/Model,
                      matching the page subtitle's "by date, scope, property, status and cost". */}
                  <TableHead>{resources.runs.columns.created}</TableHead>
                  <TableHead>{resources.runs.columns.scope}</TableHead>
                  <TableHead>{resources.runs.columns.propertyId}</TableHead>
                  <TableHead>{resources.runs.columns.status}</TableHead>
                  <TableHead>{resources.runs.columns.resultError}</TableHead>
                  <TableHead>{resources.runs.columns.duration}</TableHead>
                  <TableHead>{resources.runs.columns.cost}</TableHead>
                  <TableHead>{resources.runs.columns.model}</TableHead>
                  <TableHead className="sticky right-0 bg-card text-center">{resources.runs.columns.logs}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.runId}>
                    <TableCell className="max-w-[110px]">
                      <div className="flex items-center gap-1.5">
                        {/* min-w-0 lets this shrink below its content's natural width -- flex items
                            default to min-width:auto, which would otherwise stop `truncate` from ever
                            engaging (the classic flexbox-ellipsis footgun) and let the full run ID
                            spill into the next column instead of clipping. */}
                        <div className="min-w-0 flex-1 truncate">
                          {hasEnrichmentDetailRoute(row.scope) ? (
                            <Link
                              to={runDetailHref(row)}
                              className="font-mono text-xs text-primary hover:underline"
                              title={`${row.runId} — view enrichment detail`}
                            >
                              {row.runId}
                            </Link>
                          ) : (
                            <span className="font-mono text-xs text-muted-foreground" title={row.runId}>
                              {row.runId}
                            </span>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-auto w-auto shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          onClick={() => copyRunId(row.runId)}
                          aria-label="Copy run ID"
                          title="Copy run ID"
                        >
                          {copiedRunId === row.runId ? (
                            <Check className="h-3 w-3 text-status-good" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground" title={formatUtcTooltip(row.createdAt)}>
                      {formatRunDate(row.createdAt)}
                    </TableCell>
                    <TableCell>{scopeLabel(row.scope)}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{row.propertyId}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-auto w-auto shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          onClick={() => setPropertyId(row.propertyId)}
                          aria-label={`Filter to property ${row.propertyId}`}
                          title="Filter to this property"
                        >
                          <ZoomIn className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge variant={statusBadgeVariant(row.status)}>{statusLabel(row.status)}</Badge>
                        {row.result && <Badge variant="secondary">{outcomeLabel(row.result)}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate text-muted-foreground">
                      {resultText(row).length > RESULT_LINK_THRESHOLD ? (
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto max-w-full p-0 truncate text-left font-normal"
                          onClick={() => setOpenResultRunId(row.runId)}
                          title={resultText(row)}
                        >
                          {resultText(row)}
                        </Button>
                      ) : (
                        <span title={resultText(row)}>{resultText(row)}</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDuration(row.durationMs)}</TableCell>
                    <TableCell>{formatCost(row.costUsd)}</TableCell>
                    <TableCell className="text-muted-foreground">{row.model ?? '—'}</TableCell>
                    {/* Pinned to the right edge (sticky) so the logs action is always reachable,
                        even on a viewport too narrow to fit every column -- otherwise it silently
                        scrolls out of reach with no visible affordance that more columns exist. */}
                    <TableCell className="sticky right-0 bg-card text-center shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.3)]">
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-xs font-medium"
                        onClick={() => setOpenLogsRunId(row.runId)}
                        aria-label={resources.runs.viewLogs}
                      >
                        {row.logMessages?.length ?? 0}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={openLogsRun !== null} onOpenChange={(open) => !open && setOpenLogsRunId(null)}>
        <DialogContent>
          {openLogsRun && (
            <>
              <DialogHeader>
                <div>
                  <DialogTitle>{resources.runs.logsModalTitle}</DialogTitle>
                  <DialogDescription>
                    {openLogsRun.runId} &middot; {scopeLabel(openLogsRun.scope)} &middot; {openLogsRun.propertyId}
                  </DialogDescription>
                </div>
                <DialogCloseButton />
              </DialogHeader>
              <DialogBody>
                {/* enrichment_runs.error isn't currently reflected in logMessages (axetay/really-enrich#62)
                    -- shown here directly from the run row so a failed run's reason is never silently
                    missing from this modal, regardless of whether that backend gap is fixed. */}
                {openLogsRun.error && (
                  <div className="mb-1 flex items-baseline gap-2.5 rounded border border-status-critical/30 bg-status-critical/10 px-2 py-1.5 font-mono text-xs">
                    <span className="w-10 shrink-0 text-[9.5px] font-bold uppercase text-status-critical">
                      error
                    </span>
                    <span className="flex-1 whitespace-pre-wrap text-status-critical">{openLogsRun.error}</span>
                  </div>
                )}
                {(openLogsRun.logMessages ?? []).length === 0 && !openLogsRun.error && (
                  <p className="text-xs text-muted-foreground">{resources.runs.noLogs}</p>
                )}
                {(openLogsRun.logMessages ?? []).map((line, index) => (
                  <div key={index} className="flex items-baseline gap-2.5 font-mono text-xs">
                    <span className={cn('w-10 shrink-0 text-[9.5px] font-bold uppercase', logLevelClassName(line.level))}>
                      {line.level}
                    </span>
                    <span className="flex-1 text-muted-foreground">{line.message}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {formatRelativeTime(line.timestamp)}
                    </span>
                  </div>
                ))}
              </DialogBody>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openResultRun !== null} onOpenChange={(open) => !open && setOpenResultRunId(null)}>
        <DialogContent>
          {openResultRun && (
            <>
              <DialogHeader>
                <div>
                  <DialogTitle>{resources.runs.resultModalTitle}</DialogTitle>
                  <DialogDescription>
                    {openResultRun.runId} &middot; {scopeLabel(openResultRun.scope)} &middot; {openResultRun.propertyId}
                  </DialogDescription>
                </div>
                <DialogCloseButton />
              </DialogHeader>
              <DialogBody>
                <div className="mb-2 flex flex-wrap items-center gap-1">
                  <Badge variant={statusBadgeVariant(openResultRun.status)}>{statusLabel(openResultRun.status)}</Badge>
                  {openResultRun.result && <Badge variant="secondary">{outcomeLabel(openResultRun.result)}</Badge>}
                </div>
                <p className="whitespace-pre-wrap font-mono text-xs text-muted-foreground">
                  {resultText(openResultRun)}
                </p>
              </DialogBody>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
